/**
 * Utilitários para formatação de dados
 */

/**
 * Formata um valor para moeda brasileira (R$)
 */
export const formatarMoeda = (valor: number): string => {
  return valor.toLocaleString('pt-BR', { 
    style: 'currency', 
    currency: 'BRL' 
  });
};

/**
 * Formata uma data no formato YYYY-MM-DD para DD/MM/YYYY
 * @param data Data em formato ISO (YYYY-MM-DD) ou objeto Date
 */
export const formatarData = (data: Date | string): string => {
  if (!data) return '';
  
  // Se for um objeto Date, converter para string ISO primeiro
  let dataStr: string;
  if (data instanceof Date) {
    dataStr = data.toISOString().split('T')[0];
  } else {
    dataStr = data;
  }
  
  // Continuar com a lógica existente para string
  try {
    const [ano, mes, dia] = dataStr.split('-');
    return `${dia}/${mes}/${ano}`;
  } catch (err) {
    console.error('Erro ao formatar data:', err);
    return dataStr; // Retorna a string original se houver erro
  }
};

/**
 * Converte uma data do formato DD/MM/YYYY para YYYY-MM-DD
 */
export const converterDataParaISO = (data: string): string => {
  if (!data) return '';
  const [dia, mes, ano] = data.split('/');
  return `${ano}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}`;
};

/**
 * Obtém a data atual no formato DD/MM/YYYY
 */
export const obterDataAtual = (): string => {
  const hoje = new Date();
  const dia = String(hoje.getDate()).padStart(2, '0');
  const mes = String(hoje.getMonth() + 1).padStart(2, '0');
  const ano = hoje.getFullYear();
  return `${dia}/${mes}/${ano}`;
};

/**
 * CORREÇÃO: Valida se uma data está no formato DD/MM/YYYY
 */
export const validarFormatoData = (data: string): boolean => {
  if (!data) return false;
  
  const regex = /^\d{2}\/\d{2}\/\d{4}$/;
  if (!regex.test(data)) return false;
  
  const [dia, mes, ano] = data.split('/').map(Number);
  
  // Validar ranges
  if (mes < 1 || mes > 12) return false;
  if (dia < 1 || dia > 31) return false;
  if (ano < 1900 || ano > 2100) return false;
  
  // Validar data real (não apenas formato)
  const dateObj = new Date(ano, mes - 1, dia);
  return dateObj.getFullYear() === ano && 
         dateObj.getMonth() === mes - 1 && 
         dateObj.getDate() === dia;
};

/**
 * NOVO: Função para converter data garantindo timezone consistente
 */
export const formatarDataSegura = (data: Date | string): string => {
  if (!data) return '';
  
  let dataObj: Date;
  
  if (typeof data === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(data)) {
      const [ano, mes, dia] = data.split('-').map(Number);
      dataObj = new Date(ano, mes - 1, dia, 12, 0, 0);
    } else {
      dataObj = new Date(data);
    }
  } else {
    dataObj = new Date(data);
  }
  
  if (isNaN(dataObj.getTime())) return '';
  
  const dia = String(dataObj.getDate()).padStart(2, '0');
  const mes = String(dataObj.getMonth() + 1).padStart(2, '0');
  const ano = dataObj.getFullYear();
  
  return `${dia}/${mes}/${ano}`;
};

/**
 * Formata um CPF (11 dígitos) para o formato 000.000.000-00
 */
export const formatarCPF = (cpf: string | undefined): string => {
  if (!cpf || cpf.length !== 11) return cpf || '';
  return `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6, 9)}-${cpf.slice(9)}`;
};

/**
 * Formata um CNPJ (14 dígitos) para o formato 00.000.000/0000-00
 */
export const formatarCNPJ = (cnpj: string | undefined): string => {
  if (!cnpj || cnpj.length !== 14) return cnpj || '';
  return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5, 8)}/${cnpj.slice(8, 12)}-${cnpj.slice(12)}`;
};

/**
 * Formata um telefone brasileiro
 */
export const formatarTelefone = (telefone: string | undefined): string => {
  if (!telefone) return telefone || '';
  
  // Remove todos os caracteres não numéricos
  const nums = telefone.replace(/\D/g, '');
  
  if (nums.length === 11) {
    return `(${nums.slice(0, 2)}) ${nums.slice(2, 7)}-${nums.slice(7)}`;
  } else if (nums.length === 10) {
    return `(${nums.slice(0, 2)}) ${nums.slice(2, 6)}-${nums.slice(6)}`;
  }
  
  return telefone;
};

/**
 * Formata um CEP (8 dígitos) para o formato 00000-000
 */
export const formatarCEP = (cep: string | undefined): string => {
  if (!cep || cep.length !== 8) return cep || '';
  return `${cep.slice(0, 5)}-${cep.slice(5)}`;
};

/**
 * Remove formatação de CPF, mantendo apenas números
 */
export const removerFormatacaoCPF = (cpf: string): string => {
  return cpf.replace(/\D/g, '');
};

/**
 * Remove formatação de CNPJ, mantendo apenas números
 */
export const removerFormatacaoCNPJ = (cnpj: string): string => {
  return cnpj.replace(/\D/g, '');
};

/**
 * Remove formatação de telefone, mantendo apenas números
 */
export const removerFormatacaoTelefone = (telefone: string): string => {
  return telefone.replace(/\D/g, '');
};

/**
 * Remove formatação de CEP, mantendo apenas números
 */
export const removerFormatacaoCEP = (cep: string): string => {
  return cep.replace(/\D/g, '');
};

/**
 * Capitaliza a primeira letra de cada palavra
 */
export const formatarNome = (nome: string): string => {
  return nome.toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
};

/**
 * Formata um número para exibição com separadores de milhares
 */
export const formatarNumero = (numero: number): string => {
  return numero.toLocaleString('pt-BR');
};

/**
 * Formata uma porcentagem
 */
export const formatarPorcentagem = (valor: number, decimais: number = 1): string => {
  return `${valor.toFixed(decimais)}%`;
};

/**
 * Trunca um texto se exceder o limite especificado
 */
export const truncarTexto = (texto: string, limite: number): string => {
  if (texto.length <= limite) return texto;
  return texto.substring(0, limite) + '...';
};

/**
 * Formata a primeira letra maiúscula
 */
export const capitalizarPrimeira = (texto: string): string => {
  if (!texto) return '';
  return texto.charAt(0).toUpperCase() + texto.slice(1).toLowerCase();
};

/**
 * Formata um horário no formato HH:MM
 */
export const formatarHorario = (horario: string): string => {
  if (!horario) return '';
  
  // Se já está no formato correto, retorna
  if (/^\d{2}:\d{2}$/.test(horario)) return horario;
  
  // Remove caracteres especiais e formata
  const nums = horario.replace(/\D/g, '');
  if (nums.length >= 4) {
    return `${nums.slice(0, 2)}:${nums.slice(2, 4)}`;
  }
  
  return horario;
};

/**
 * Calcula a idade baseada na data de nascimento
 */
export const calcularIdade = (dataNascimento: string): number => {
  if (!dataNascimento) return 0;
  
  const nascimento = new Date(dataNascimento);
  const hoje = new Date();
  
  let idade = hoje.getFullYear() - nascimento.getFullYear();
  const mesAtual = hoje.getMonth();
  const diaAtual = hoje.getDate();
  const mesNascimento = nascimento.getMonth();
  const diaNascimento = nascimento.getDate();
  
  if (mesAtual < mesNascimento || (mesAtual === mesNascimento && diaAtual < diaNascimento)) {
    idade--;
  }
  
  return idade;
};
